// Service for transforming planogram data between API format
// and internal grid format.

import {
    createEmptyCell,
    PlanogramCellModel,
} from '../model/PlanogramCellModel';

import {
    normalizeWeightOrVolume,
    WeightOrVolume,
} from '../model/PlanogramProductModel';

import {
    convertDateTimeToApiFormat,
} from '../../../utils/date/DateUtils';

export class PlanogramTransformUtils {
    /**
     * Supports values entered as:
     *
     * - weightOrVolume: { value: 330, unit: 'ML' }
     * - weightVolume: 330, unitOfMeasurement: 'ML'
     * - weightVolume: '330 ML'
     */
    static getCellWeightOrVolume(
        cell: Record<string, any>,
    ): WeightOrVolume | null {
        const product =
            cell?.product &&
            typeof cell.product === 'object'
                ? cell.product
                : {};

        const structuredValue =
            cell?.weightOrVolume ??
            product?.weightOrVolume ??
            null;

        const legacyValue =
            cell?.weightVolume ??
            product?.weightVolume ??
            null;

        const legacyUnit =
            cell?.unitOfMeasurement ??
            product?.unitOfMeasurement ??
            null;

        /*
         * First handle the structured API value and the legacy
         * value/unit pair.
         */
        const normalizedValue =
            normalizeWeightOrVolume(
                structuredValue,
                legacyValue,
                legacyUnit,
            );

        if (normalizedValue) {
            return normalizedValue;
        }

        /*
         * Inline planogram editing may temporarily store the complete
         * value in weightVolume, for example "330 ML".
         */
        if (typeof legacyValue !== 'string') {
            return null;
        }

        const input = legacyValue.trim();

        if (!input) {
            return null;
        }

        const match = input.match(
            /^(-?\d+(?:[.,]\d+)?)\s*(.*)$/,
        );

        if (!match) {
            return null;
        }

        const value = Number(
            match[1].replace(',', '.'),
        );

        const unit =
            match[2].trim() ||
            String(legacyUnit ?? '').trim();

        if (!Number.isFinite(value) || !unit) {
            return null;
        }

        return {
            value,
            unit,
        };
    }

    /**
     * Transform API data to internal planogram grid format.
     *
     * @param apiData planogram data returned by the API
     * @returns two-dimensional planogram grid
     */
    static apiToGrid(apiData: Record<string, any>) {
        if (!apiData?.rows || !apiData?.columns) {
            console.warn(
                'Invalid API data for grid transformation',
            );

            return [];
        }

        console.log(
            'Transforming API data to grid format:',
            apiData,
        );

        const grid: PlanogramCellModel[][] = [];

        for (
            let rowIndex = 0;
            rowIndex < apiData.rows;
            rowIndex += 1
        ) {
            const row: PlanogramCellModel[] = [];

            for (
                let colIndex = 0;
                colIndex < apiData.columns;
                colIndex += 1
            ) {
                row.push(
                    createEmptyCell({
                        rowIndex,
                        colIndex,
                    }),
                );
            }

            grid.push(row);
        }

        const apiCells = Array.isArray(apiData.cells)
            ? apiData.cells
            : [];

        console.log(
            'Processing API cells:',
            apiCells,
        );

        apiCells.forEach(
            (cell: Record<string, any>) => {
                /*
                 * Normalize both initialization identifiers and the
                 * new weightOrVolume API property.
                 */
                const normalizedWeightOrVolume =
                    normalizeWeightOrVolume(
                        cell?.weightOrVolume ??
                            cell?.weight_or_volume,
                        cell?.weightVolume ??
                            cell?.weight_volume,
                        cell?.unitOfMeasurement ??
                            cell?.unit_of_measurement,
                    );

                const normalizedCell: Record<string, any> = {
                    ...cell,
                    mdbId:
                        cell?.mdbId ??
                        cell?.idMdb ??
                        null,
                    evaDtsId:
                        cell?.evaDtsId ??
                        cell?.idEvaDts ??
                        null,
                    weightOrVolume:
                        normalizedWeightOrVolume,
                    weightVolume:
                        normalizedWeightOrVolume?.value ??
                        cell?.weightVolume ??
                        cell?.weight_volume ??
                        null,
                    unitOfMeasurement:
                        normalizedWeightOrVolume?.unit ??
                        cell?.unitOfMeasurement ??
                        cell?.unit_of_measurement ??
                        null,
                };

                const rowIndex =
                    Number(cell.rowNumber) - 1;

                const colIndex =
                    Number(cell.columnNumber) - 1;

                const isValidPosition =
                    rowIndex >= 0 &&
                    rowIndex < apiData.rows &&
                    colIndex >= 0 &&
                    colIndex < apiData.columns;

                if (!isValidPosition) {
                    return;
                }

                if (
                    cell.colspan &&
                    cell.colspan > 1
                ) {
                    console.log(
                        `Creating merged cell at [${rowIndex}][${colIndex}] with colspan: ${cell.colspan}`,
                    );

                    const mergedCell =
                        PlanogramCellModel.fromApiCell({
                            apiCell:
                                normalizedCell,
                            rowIndex,
                            colIndex,
                        });

                    mergedCell.merged = true;

                    mergedCell.mergeData = {
                        colspan: cell.colspan,
                        mergedCells: Array.from(
                            {
                                length:
                                    cell.colspan,
                            },
                            (_, index) =>
                                `${rowIndex}-${colIndex + index}`,
                        ),
                        sourceCell: {
                            rowIndex,
                            colIndex,
                        },
                        targetCells: Array.from(
                            {
                                length:
                                    cell.colspan - 1,
                            },
                            (_, index) => ({
                                rowIndex,
                                colIndex:
                                    colIndex +
                                    1 +
                                    index,
                            }),
                        ),
                    };

                    mergedCell.mergedMdbIds =
                        cell?.mergedMdbIds ?? null;

                    mergedCell.mergedEvaDtsIds =
                        cell?.mergedEvaDtsIds ?? null;

                    const mergedCellMergeData =
                        mergedCell.mergeData as Record<string, any>;

                    mergedCellMergeData.mergedMdbIds =
                        cell?.mergedMdbIds ?? null;

                    mergedCellMergeData.mergedEvaDtsIds =
                        cell?.mergedEvaDtsIds ?? null;

                    if (
                        !normalizedCell.productExternalId
                    ) {
                        mergedCell.type =
                            'empty_merged';
                    }

                    grid[rowIndex][colIndex] =
                        mergedCell;

                    console.log(
                        'Created merged cell:',
                        mergedCell,
                    );

                    for (
                        let index = 1;
                        index < cell.colspan;
                        index += 1
                    ) {
                        const targetColIndex =
                            colIndex + index;

                        if (
                            targetColIndex >=
                            apiData.columns
                        ) {
                            continue;
                        }

                        const hiddenCell =
                            createEmptyCell({
                                rowIndex,
                                colIndex:
                                    targetColIndex,
                            });

                        hiddenCell.merged = true;

                        hiddenCell.mergeData = {
                            colspan:
                                cell.colspan,
                            mergedCells:
                                Array.from(
                                    {
                                        length:
                                            cell.colspan,
                                    },
                                    (
                                        _,
                                        mergedIndex,
                                    ) =>
                                        `${rowIndex}-${colIndex + mergedIndex}`,
                                ),
                            isMerged: true,
                            sourceCell: {
                                rowIndex,
                                colIndex,
                            },
                            mergedMdbIds:
                                cell?.mergedMdbIds ??
                                null,
                            mergedEvaDtsIds:
                                cell?.mergedEvaDtsIds ??
                                null,
                        };

                        grid[rowIndex][targetColIndex] =
                            hiddenCell;

                        console.log(
                            `Created hidden merged cell at [${rowIndex}][${targetColIndex}]:`,
                            hiddenCell,
                        );
                    }

                    return;
                }

                console.log(
                    `Creating single cell at [${rowIndex}][${colIndex}]`,
                );

                grid[rowIndex][colIndex] =
                    PlanogramCellModel.fromApiCell({
                        apiCell: normalizedCell,
                        rowIndex,
                        colIndex,
                    });
            },
        );

        console.log(
            'Final grid structure:',
            grid,
        );

        return grid;
    }

    /**
     * Transform the internal planogram grid into the API format.
     *
     * weightOrVolume is added to every product cell:
     *
     * {
     *     value: 330,
     *     unit: 'ML'
     * }
     */
    static gridToApi(
        planogramData: Record<string, any>,
    ) {
        if (
            !planogramData?.cells ||
            !Array.isArray(planogramData.cells)
        ) {
            console.warn(
                'Invalid planogram data for API transformation',
            );

            return [];
        }

        const transformedCells:
            Record<string, any>[] = [];

        console.log(
            'Transforming grid to API format. Grid structure:',
            planogramData.cells,
        );

        planogramData.cells.forEach(
            (
                row: Record<string, any>[],
                rowIndex: number,
            ) => {
                row?.forEach?.(
                    (
                        cell: Record<string, any>,
                        colIndex: number,
                    ) => {
                        /*
                         * Hidden parts of merged cells are not sent.
                         * Only the visible source cell is persisted.
                         */
                        if (
                            cell.merged &&
                            cell.mergeData?.isMerged
                        ) {
                            console.log(
                                `Skipping hidden merged cell at [${rowIndex}][${colIndex}]`,
                            );

                            return;
                        }

                        if (
                            cell.merged &&
                            cell.mergeData?.colspan > 1
                        ) {
                            const mergedApiCell:
                                Record<string, any> = {
                                    id: `${rowIndex}-${colIndex}`,
                                    rowNumber:
                                        rowIndex + 1,
                                    columnNumber:
                                        colIndex + 1,
                                    colspan:
                                        cell.mergeData
                                            .colspan,
                                };

                            if (cell.mdbId) {
                                mergedApiCell.idMdb =
                                    cell.mdbId;
                            }

                            if (cell.evaDtsId) {
                                mergedApiCell.idEvaDts =
                                    cell.evaDtsId;
                            }

                            /*
                             * Initialization identifiers must also be
                             * included using their regular property names.
                             */
                            mergedApiCell.mdbId =
                                cell.mdbId;

                            mergedApiCell.evaDtsId =
                                cell.evaDtsId;

                            mergedApiCell.externalId =
                                cell.externalId;

                            mergedApiCell.mergedMdbIds =
                                cell.mergedMdbIds ??
                                cell?.mergeData
                                    ?.mergedMdbIds ??
                                null;

                            mergedApiCell.mergedEvaDtsIds =
                                cell.mergedEvaDtsIds ??
                                cell?.mergeData
                                    ?.mergedEvaDtsIds ??
                                null;

                            if (
                                cell.type ===
                                    'product' &&
                                cell.product
                            ) {
                                mergedApiCell.productExternalId =
                                    cell.product.id;

                                mergedApiCell.price =
                                    cell.price ??
                                    cell.product.price;

                                mergedApiCell.minQuantity =
                                    cell.minQuantity ??
                                    0;

                                mergedApiCell.maxQuantity =
                                    cell.maxQuantity ??
                                    0;

                                mergedApiCell.currency =
                                    cell.currency ??
                                    cell.product
                                        .currency;

                                mergedApiCell.categoryName =
                                    cell.product
                                        .categoryName;

                                mergedApiCell.categoryColor =
                                    cell.product
                                        .categoryColor;

                                mergedApiCell.ean =
                                    cell.product.ean;

                                mergedApiCell.productPhotoUrl =
                                    cell.product
                                        .photoUrl;

                                mergedApiCell.hasPhoto =
                                    cell.product
                                        .hasPhoto;

                                mergedApiCell.productName =
                                    cell.product.name;

                                mergedApiCell.weightOrVolume =
                                    this.getCellWeightOrVolume(
                                        cell,
                                    );

                                console.log(
                                    'Merged cell effectiveDate before conversion:',
                                    cell.effectiveDate,
                                    'product effectiveDate:',
                                    cell.product
                                        .effectiveDate,
                                );

                                mergedApiCell.effectiveDate =
                                    convertDateTimeToApiFormat(
                                        cell.effectiveDate ??
                                            cell.product
                                                .effectiveDate,
                                    );
                            }

                            transformedCells.push(
                                mergedApiCell,
                            );

                            console.log(
                                'Created merged API cell:',
                                mergedApiCell,
                            );

                            return;
                        }

                        /*
                         * Regular, unmerged product cell.
                         */
                        if (
                            cell.type === 'product' &&
                            cell.product
                        ) {
                            const singleProductApiCell:
                                Record<string, any> = {
                                    productExternalId:
                                        cell.product.id,
                                    id: `${rowIndex}-${colIndex}`,
                                    rowNumber:
                                        rowIndex + 1,
                                    columnNumber:
                                        colIndex + 1,
                                    colspan: 1,
                                    price:
                                        cell.price ??
                                        cell.product
                                            .price,
                                    minQuantity:
                                        cell.minQuantity ??
                                        0,
                                    maxQuantity:
                                        cell.maxQuantity ??
                                        0,
                                    currency:
                                        cell.currency ??
                                        cell.product
                                            .currency,
                                    mdbId:
                                        cell.mdbId,
                                    evaDtsId:
                                        cell.evaDtsId,
                                    externalId:
                                        cell.externalId,
                                    effectiveDate:
                                        convertDateTimeToApiFormat(
                                            cell.effectiveDate ??
                                                cell.product
                                                    .effectiveDate,
                                        ),
                                    categoryName:
                                        cell.product
                                            .categoryName,
                                    categoryColor:
                                        cell.product
                                            .categoryColor,
                                    ean:
                                        cell.product.ean,
                                    productPhotoUrl:
                                        cell.product
                                            .photoUrl,
                                    hasPhoto:
                                        cell.product
                                            .hasPhoto,
                                    productName:
                                        cell.product.name,
                                    weightOrVolume:
                                        this.getCellWeightOrVolume(
                                            cell,
                                        ),
                                };

                            transformedCells.push(
                                singleProductApiCell,
                            );

                            console.log(
                                'Created single product API cell:',
                                singleProductApiCell,
                            );

                            return;
                        }

                        /*
                         * Empty initialization cells must still be sent
                         * when they contain MDB or EVA-DTS identifiers.
                         */
                        if (
                            cell.mdbId ||
                            cell.evaDtsId
                        ) {
                            const initializationApiCell:
                                Record<string, any> = {
                                    productExternalId:
                                        null,
                                    id: `${rowIndex}-${colIndex}`,
                                    rowNumber:
                                        rowIndex + 1,
                                    columnNumber:
                                        colIndex + 1,
                                    colspan: 1,
                                    price: null,
                                    minQuantity: null,
                                    maxQuantity: null,
                                    mdbId:
                                        cell.mdbId,
                                    evaDtsId:
                                        cell.evaDtsId,
                                    externalId:
                                        cell.externalId,
                                    effectiveDate:
                                        convertDateTimeToApiFormat(
                                            cell.effectiveDate,
                                        ),
                                    weightOrVolume:
                                        null,
                                };

                            transformedCells.push(
                                initializationApiCell,
                            );

                            console.log(
                                'Created single initialization API cell:',
                                initializationApiCell,
                            );
                        }
                    },
                );
            },
        );

        console.log(
            'Final transformed API cells:',
            transformedCells,
        );

        return transformedCells;
    }

    /**
     * Create a planogram object for an API save/update request.
     */
    static createApiPlanogram(
        planogramData: Record<string, any>,
    ): Record<string, any> {
        return {
            externalId:
                planogramData.externalId,
            externalSystemId:
                planogramData.externalSystemId,
            deviceExternalId:
                planogramData.deviceExternalId,
            planogramExternalId:
                planogramData.planogramExternalId,
            name:
                planogramData.name,
            rows:
                planogramData.rows,
            columns:
                planogramData.columns,
            cells:
                this.gridToApi(planogramData),
        };
    }

    /**
     * Create a planogram object for an API creation request.
     */
    static createApiPlanogramForCreation(
        planogramData: Record<string, any>,
    ): Record<string, any> {
        return {
            ...this.createApiPlanogram(
                planogramData,
            ),
            status: 'TO_BE_DONE',
        };
    }

    /**
     * Verify merged-cell transformation.
     */
    static checkMergedCellsTransformation(
        planogramData: Record<string, any>,
    ) {
        const originalCells =
            planogramData.cells;

        const apiCells =
            this.gridToApi(planogramData);

        let mergedCellsCount = 0;
        let totalProductCells = 0;

        originalCells.forEach(
            (
                row: Record<string, any>[],
                rowIndex: number,
            ) => {
                row.forEach(
                    (
                        cell: Record<string, any>,
                        colIndex: number,
                    ) => {
                        if (
                            cell.type ===
                                'product' &&
                            cell.product
                        ) {
                            totalProductCells += 1;

                            if (
                                cell.merged &&
                                cell.mergeData
                                    ?.colspan > 1
                            ) {
                                mergedCellsCount += 1;

                                console.log(
                                    `Found merged cell at [${rowIndex}][${colIndex}] with colspan: ${cell.mergeData.colspan}`,
                                );
                            }
                        }
                    },
                );
            },
        );

        console.log(
            `Original grid: ${totalProductCells} product cells, ${mergedCellsCount} merged cells`,
        );

        console.log(
            `API format: ${apiCells.length} cells`,
        );

        const mergedApiCells =
            apiCells.filter(
                (
                    cell:
                        Record<string, any>,
                ) => cell.colspan > 1,
            );

        console.log(
            'API merged cells:',
            mergedApiCells,
        );

        const result = {
            originalProductCells:
                totalProductCells,
            originalMergedCells:
                mergedCellsCount,
            apiCells:
                apiCells.length,
            apiMergedCells:
                mergedApiCells.length,
            success:
                mergedApiCells.length >=
                mergedCellsCount,
        };

        console.log(
            'Test result:',
            result,
        );

        return result;
    }

    /**
     * Verify API-to-grid transformation with merged cells.
     */
    static testApiToGridTransformation(
        apiData: Record<string, any>,
    ) {
        const apiCells =
            apiData.cells || [];

        const grid =
            this.apiToGrid(apiData);

        let apiMergedCellsCount = 0;

        const totalApiCells =
            apiCells.length;

        apiCells.forEach(
            (cell: Record<string, any>) => {
                if (
                    cell.colspan &&
                    cell.colspan > 1
                ) {
                    apiMergedCellsCount += 1;

                    console.log(
                        `Found API merged cell: rowNumber=${cell.rowNumber}, columnNumber=${cell.columnNumber}, colspan=${cell.colspan}`,
                    );
                }
            },
        );

        let gridMergedCellsCount = 0;
        let gridHiddenCellsCount = 0;
        let totalGridProductCells = 0;

        grid.forEach(
            (
                row: PlanogramCellModel[],
                rowIndex: number,
            ) => {
                row.forEach(
                    (
                        cell: PlanogramCellModel,
                        colIndex: number,
                    ) => {
                        if (
                            cell.type ===
                                'product' &&
                            cell.product
                        ) {
                            totalGridProductCells += 1;

                            if (
                                cell.merged &&
                                (
                                    cell.mergeData as
                                        | Record<
                                            string,
                                            any
                                        >
                                        | null
                                )?.colspan > 1
                            ) {
                                gridMergedCellsCount += 1;

                                console.log(
                                    `Found grid merged cell at [${rowIndex}][${colIndex}] with colspan: ${
                                        (
                                            cell.mergeData as Record<
                                                string,
                                                any
                                            >
                                        ).colspan
                                    }`,
                                );
                            }
                        }

                        if (
                            cell.merged &&
                            (
                                cell.mergeData as
                                    | Record<
                                        string,
                                        any
                                    >
                                    | null
                            )?.isMerged
                        ) {
                            gridHiddenCellsCount += 1;

                            console.log(
                                `Found grid hidden cell at [${rowIndex}][${colIndex}]`,
                            );
                        }
                    },
                );
            },
        );

        console.log(
            `API data: ${totalApiCells} cells, ${apiMergedCellsCount} merged cells`,
        );

        console.log(
            `Grid result: ${totalGridProductCells} product cells, ${gridMergedCellsCount} merged cells, ${gridHiddenCellsCount} hidden cells`,
        );

        const result = {
            apiCells:
                totalApiCells,
            apiMergedCells:
                apiMergedCellsCount,
            gridProductCells:
                totalGridProductCells,
            gridMergedCells:
                gridMergedCellsCount,
            gridHiddenCells:
                gridHiddenCellsCount,
            success:
                apiMergedCellsCount ===
                    gridMergedCellsCount &&
                gridHiddenCellsCount >= 0,
        };

        console.log(
            'Test result:',
            result,
        );

        return result;
    }
}